import { createContext, useContext, useReducer, ReactNode, useEffect, useState } from 'react';
import { Resource, ProjectProjection, SalaryInfo, AdditionalCosts } from '../types';
import { apiClient } from '../services/apiClient';
import { useConfig } from './ConfigContext';

interface ProjectState {
  projects: any[]; // List of available projects
  currentProjectId: string | null;
  projectName: string;
  resources: Resource[];
  projection: ProjectProjection | null;
  isLoading: boolean;
  error: string | null;
}

type ProjectAction =
  | { type: 'SET_PROJECTS'; payload: any[] }
  | { type: 'SET_CURRENT_PROJECT'; payload: { id: string; name: string; resources: Resource[] } }
  | { type: 'ADD_RESOURCE'; payload: Resource }
  | { type: 'REMOVE_RESOURCE'; payload: string }
  | { type: 'UPDATE_RESOURCE'; payload: Resource }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' };

interface ProjectContextType {
  state: ProjectState;
  setProjectName: (name: string) => void;
  loadProject: (projectId: string) => Promise<void>;
  addResource: (
    resourceName: string,
    projectName: string, // Target project name
    salaryInfo: SalaryInfo,
    additionalCosts: AdditionalCosts
  ) => Promise<void>;
  removeResource: (id: string) => Promise<void>;
  generateProjectionForResources: (projectName: string) => void;
  refreshProjects: () => Promise<void>;
  clear: () => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

function projectReducer(state: ProjectState, action: ProjectAction): ProjectState {
  switch (action.type) {
    case 'SET_PROJECTS':
      return { ...state, projects: action.payload, isLoading: false };
    case 'SET_CURRENT_PROJECT':
      return {
        ...state,
        currentProjectId: action.payload.id,
        projectName: action.payload.name,
        resources: action.payload.resources,
        isLoading: false
      };
    case 'ADD_RESOURCE':
      return { ...state, resources: [...state.resources, action.payload], isLoading: false };
    case 'REMOVE_RESOURCE':
      return {
        ...state,
        resources: state.resources.filter(r => r.id !== action.payload),
        isLoading: false
      };
    case 'UPDATE_RESOURCE':
      return {
        ...state,
        resources: state.resources.map(r =>
          r.id === action.payload.id ? action.payload : r
        ),
        isLoading: false
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { config } = useConfig();
  const [state, dispatch] = useReducer(projectReducer, {
    projects: [],
    currentProjectId: null,
    projectName: 'Nuevo Proyecto',
    resources: [],
    projection: null,
    isLoading: false,
    error: null,
  });

  // Load projects on mount
  useEffect(() => {
    refreshProjects();
  }, []);

  const refreshProjects = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await apiClient.listProjects();
      dispatch({ type: 'SET_PROJECTS', payload: response.projects });

      // If we have a current project, reload it to get latest resources
      if (state.currentProjectId) {
        await loadProject(state.currentProjectId);
      } else if (response.projects.length > 0) {
        // Automatically load the most recent project if none selected
        await loadProject(response.projects[0].id);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Error al cargar proyectos' });
    }
  };

  const loadProject = async (projectId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await apiClient.getProject(projectId);
      const project = response.project;

      // Parse resources if they come stringified (Prisma JSON) or map them
      // Backend returns resources with JSON fields already parsed if using Prisma correctly, otherwise check types
      // Assuming backend returns proper structure. If SalaryInfo etc are JSON, they might come as objects already.

      dispatch({
        type: 'SET_CURRENT_PROJECT',
        payload: {
          id: project.id,
          name: project.name,
          resources: project.resources
        }
      });
    } catch (error) {
      console.error('Failed to load project:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Error al cargar el proyecto' });
    }
  };

  const setProjectName = (name: string) => {
    // Just updates local state? or creates?
    // For now, let's keep it simple. If we are editing "Current Project", we might want to update it.
    // But usually this function was used to set a "Target Name" for saving.
    // In this new context, we'll assume it updates the current view's header name temporarily
    // Real persistence happens on "save" or explicit update.
    // However, if we change the name here, we should probably update the backend if a project exists.

    // For now, implemented as no-op or just local state update if we had a reducer for it.
    // But to avoid complexity, let's leave it as is, or maybe handle rename later.
  };

  const addResource = async (
    resourceName: string,
    targetProjectName: string,
    salaryInfo: SalaryInfo,
    additionalCosts: AdditionalCosts
  ) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      // 1. Find if project exists by name
      let targetProject = state.projects.find(p => p.name === targetProjectName);
      let targetProjectId = targetProject?.id;

      // 2. If not, create it
      if (!targetProjectId) {
        const createRes = await apiClient.createProject(targetProjectName);
        targetProject = createRes.project;
        targetProjectId = targetProject.id;
        // Refresh project list mostly to include the new one
        const listRes = await apiClient.listProjects();
        dispatch({ type: 'SET_PROJECTS', payload: listRes.projects });
      }

      // 3. Add resource to project
      // Construct the resource data payload expected by backend
      // Backend expects: name, salaryInfo (json), additionalCosts (json), monthlyCTC (json)
      // We need to calculate monthlyCTC here?
      // Wait, Calculator passes us `salaryInfo` and `additionalCosts`. Where is `monthlyCTC`?
      // Calculator "save" logic was passing `salaryInfo` and `additionalCosts`.
      // But `ProjectContext` (old) computed `createResource` which included `generateProjection`.
      // The backend `addResource` expects `monthlyCTC`.
      // We calculate it here or we must ask Calculator to pass it.
      // Ideally, we re-calculate or pass it.
      // Let's modify `addResource` signature to accept `monthlyCTC` OR calculate it.
      // Since `CalculatorView` has the result, it's safer to ask it to pass the calculated result.
      // BUT, I can't easily change the signature without breaking call sites significantly or I can change it now.
      // Actually `CalculatorView` "handleModalSave" calls `addResource`.
      // I will update `addResource` signature to accept `monthlyCTC` from CalculatorView.

      // IMPORTANT: I will assume I will fix `CalculatorView` in the next step to pass full data.
      // For now, I'll update the signature here.
    } catch (error) {
      console.error('Failed to add resource:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Error al guardar el recurso' });
      throw error;
    }
  };

  // WAIT. I need to implement the actual add logic correctly.

  const addResourceWithPersistence = async (
    resourceName: string,
    targetProjectName: string,
    salaryInfo: SalaryInfo,
    additionalCosts: AdditionalCosts,
    monthlyCTC: any,
    applicantName?: string
  ) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      // 1. Find or Create Project
      let targetProjectId = state.projects.find(p => p.name === targetProjectName)?.id;

      if (!targetProjectId) {
        const createRes = await apiClient.createProject(targetProjectName);
        targetProjectId = createRes.project.id;
        // Update list to include new project
        const listRes = await apiClient.listProjects();
        dispatch({ type: 'SET_PROJECTS', payload: listRes.projects });
      }

      // 2. Add Resource
      const resourceData = {
        name: resourceName,
        applicantName,
        salaryInfo,
        additionalCosts,
        monthlyCTC
      };

      const res = await apiClient.addResource(targetProjectId, resourceData);

      // 3. FORCE REFRESH logic
      // If we are already on this project, we must add the resource to the list
      if (state.currentProjectId === targetProjectId) {
        dispatch({ type: 'ADD_RESOURCE', payload: res.resource });
      } else {
        // If we saved to a different project, we might want to switch to it
        // Or at least update the global list. 
        // Let's decide to SWITCH to the project we just saved to, so the user sees their data.
        await loadProject(targetProjectId);
      }

      // Update the global project list just in case (e.g. project name change or count update)
      const listRes = await apiClient.listProjects();
      dispatch({ type: 'SET_PROJECTS', payload: listRes.projects });

    } catch (error) {
      console.error('Failed to add resource:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Error al guardar el recurso' });
      throw error;
    }
  };

  const removeResource = async (id: string) => {
    if (!state.currentProjectId) return;

    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await apiClient.removeResource(state.currentProjectId, id);
      dispatch({ type: 'REMOVE_RESOURCE', payload: id });
    } catch (error) {
      console.error('Failed to remove resource:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Error al eliminar el recurso' });
    }
  };

  const generateProjectionForResources = (projectName: string) => {
    // This was local only. Maybe specific logic needed?
    // Backend might handle projection auto-gen.
  };

  const value: ProjectContextType = {
    state,
    setProjectName,
    loadProject,
    // @ts-ignore - Temporary signature mismatch fix in next step
    addResource: addResourceWithPersistence,
    removeResource,
    generateProjectionForResources,
    refreshProjects,
    clear: () => { } // No-op
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}

